import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, CurrencyDollar, Target, FileText, Plus, CheckCircle, Calendar, Receipt, Sparkle, Lightning, BookOpen, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

const PostGrantDetail = () => {
  const { postGrantId } = useParams();
  const navigate = useNavigate();
  const [postGrant, setPostGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingBudget, setGeneratingBudget] = useState(false);
  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [smartBudget, setSmartBudget] = useState(null);
  const [spendingGuide, setSpendingGuide] = useState(null);
  
  // Dialog states
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [flashReportDialogOpen, setFlashReportDialogOpen] = useState(false);
  
  // Form states
  const [newBudget, setNewBudget] = useState({ name: "", allocated: "", spent: "" });
  const [newMilestone, setNewMilestone] = useState({ title: "", description: "", due_date: "" });
  const [newReport, setNewReport] = useState({ title: "", content: "", report_type: "progress" });
  const [newExpense, setNewExpense] = useState({ budget_item_id: "", description: "", amount: "", date: "", receipt_note: "" });
  const [flashReportType, setFlashReportType] = useState("progress");
  const [flashReportNotes, setFlashReportNotes] = useState("");

  useEffect(() => {
    const fetchPostGrant = async () => {
      try {
        const res = await axios.get(`${API}/post-grants/${postGrantId}`);
        setPostGrant(res.data);
      } catch (error) {
        console.error("Error fetching post-grant:", error);
        toast.error("Failed to load project");
        navigate("/post-grants");
      } finally {
        setLoading(false);
      }
    };

    fetchPostGrant();
  }, [postGrantId, navigate]);

  const addBudgetItem = async () => {
    if (!newBudget.name || !newBudget.allocated) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/budget`, {
        name: newBudget.name,
        allocated: parseFloat(newBudget.allocated),
        spent: parseFloat(newBudget.spent) || 0
      });

      setPostGrant(res.data);
      setBudgetDialogOpen(false);
      setNewBudget({ name: "", allocated: "", spent: "" });
      toast.success("Budget item added");
    } catch (error) {
      toast.error("Failed to add budget item");
    }
  };

  const addMilestone = async () => {
    if (!newMilestone.title || !newMilestone.due_date) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/milestones`, {
        title: newMilestone.title,
        description: newMilestone.description,
        due_date: newMilestone.due_date,
        completed: false
      });

      setPostGrant(res.data);
      setMilestoneDialogOpen(false);
      setNewMilestone({ title: "", description: "", due_date: "" });
      toast.success("Milestone added");
    } catch (error) {
      toast.error("Failed to add milestone");
    }
  };

  const toggleMilestone = async (milestoneId, completed) => {
    try {
      const res = await axios.put(`${API}/post-grants/${postGrantId}/milestones/${milestoneId}`, {
        completed: !completed
      });

      setPostGrant(res.data);
      toast.success(completed ? "Milestone reopened" : "Milestone completed!");
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const addReport = async () => {
    if (!newReport.title || !newReport.content) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/reports`, newReport);

      setPostGrant(res.data);
      setReportDialogOpen(false);
      setNewReport({ title: "", content: "", report_type: "progress" });
      toast.success("Report added");
    } catch (error) {
      toast.error("Failed to add report");
    }
  };

  const addExpense = async () => {
    if (!newExpense.budget_item_id || !newExpense.description || !newExpense.amount || !newExpense.date) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/expenses`, {
        budget_item_id: newExpense.budget_item_id,
        description: newExpense.description,
        amount: parseFloat(newExpense.amount),
        date: newExpense.date,
        receipt_note: newExpense.receipt_note
      });

      setPostGrant(res.data);
      setExpenseDialogOpen(false);
      setNewExpense({ budget_item_id: "", description: "", amount: "", date: "", receipt_note: "" });
      toast.success("Expense added");
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const generateFlashReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/flash-report`, {
        report_type: flashReportType,
        additional_notes: flashReportNotes
      });

      setPostGrant(res.data.post_grant);
      setFlashReportDialogOpen(false);
      setFlashReportNotes("");
      toast.success("Flash Report generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateSmartBudget = async () => {
    setGeneratingBudget(true);
    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/smart-budget`, {});
      setSmartBudget(res.data);
      toast.success("Smart Budget template generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate budget template");
    } finally {
      setGeneratingBudget(false);
    }
  };

  const applyBudgetTemplate = async (items) => {
    try {
      for (const item of items) {
        await axios.post(`${API}/post-grants/${postGrantId}/budget`, {
          name: item.name,
          allocated: item.allocated,
          spent: 0
        });
      }
      // Refresh the post-grant data
      const res = await axios.get(`${API}/post-grants/${postGrantId}`);
      setPostGrant(res.data);
      setSmartBudget(null);
      toast.success("Budget template applied!");
    } catch (error) {
      toast.error("Failed to apply budget template");
    }
  };

  const generateSpendingGuide = async () => {
    setGeneratingGuide(true);
    try {
      const res = await axios.post(`${API}/post-grants/${postGrantId}/spending-guide`, {});
      setSpendingGuide(res.data);
      toast.success("Spending guide generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate spending guide");
    } finally {
      setGeneratingGuide(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!postGrant) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-bold mb-2">Project not found</h2>
          <Button onClick={() => navigate("/post-grants")} variant="outline">
            Back to Projects
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const totalAllocated = postGrant.budget_items?.reduce((sum, item) => sum + item.allocated, 0) || 0;
  const totalSpent = postGrant.budget_items?.reduce((sum, item) => sum + (item.spent || 0), 0) || 0;
  const completedMilestones = postGrant.milestones?.filter(m => m.completed).length || 0;
  const totalMilestones = postGrant.milestones?.length || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="post-grant-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/post-grants")}
          className="text-zinc-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className={`status-${postGrant.status} px-2 py-0.5 rounded-sm text-xs font-mono uppercase`}>
              {postGrant.status}
            </span>
            <h1 className="text-3xl font-bold tracking-tight mt-2">{postGrant.grant_title}</h1>
            <div className="flex items-center gap-2 text-emerald-400 font-mono mt-2">
              <CurrencyDollar className="w-5 h-5" />
              <span className="text-xl">${postGrant.awarded_amount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Budget Allocated</p>
              <p className="text-2xl font-mono text-emerald-400">${totalAllocated.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Budget Spent</p>
              <p className="text-2xl font-mono text-white">${totalSpent.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Milestones</p>
              <p className="text-2xl font-mono text-white">{completedMilestones}/{totalMilestones}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Reports</p>
              <p className="text-2xl font-mono text-white">{postGrant.reports?.length || 0}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Budget Tracking */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CurrencyDollar weight="duotone" className="w-5 h-5 text-emerald-400" />
                  Budget Tracking
                </CardTitle>
                <div className="flex gap-2">
                  {/* Smart Budget Button */}
                  {(!postGrant.budget_items || postGrant.budget_items.length === 0) && (
                    <Button
                      size="sm"
                      onClick={generateSmartBudget}
                      disabled={generatingBudget}
                      className="bg-black border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm"
                      data-testid="smart-budget-btn"
                    >
                      {generatingBudget ? (
                        <><div className="loading-spinner mr-2" style={{width:14,height:14}} />Generating...</>
                      ) : (
                        <><Sparkle className="w-4 h-4 mr-1" />Smart Budget</>
                      )}
                    </Button>
                  )}
                  <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="add-budget-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="bg-zinc-950 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle>Add Budget Item</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Item Name *</Label>
                        <Input 
                          value={newBudget.name}
                          onChange={(e) => setNewBudget(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Equipment"
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="input-budget-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Allocated ($) *</Label>
                          <Input 
                            type="number"
                            value={newBudget.allocated}
                            onChange={(e) => setNewBudget(prev => ({ ...prev, allocated: e.target.value }))}
                            placeholder="1000"
                            className="bg-zinc-900 border-zinc-800"
                            data-testid="input-budget-allocated"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Spent ($)</Label>
                          <Input 
                            type="number"
                            value={newBudget.spent}
                            onChange={(e) => setNewBudget(prev => ({ ...prev, spent: e.target.value }))}
                            placeholder="0"
                            className="bg-zinc-900 border-zinc-800"
                            data-testid="input-budget-spent"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={addBudgetItem}
                        className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                        data-testid="submit-budget-btn"
                      >
                        Add Item
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {postGrant.budget_items?.length === 0 ? (
                <p className="text-zinc-500 text-center py-4">No budget items yet</p>
              ) : (
                <div className="space-y-4">
                  {postGrant.budget_items?.map((item) => (
                    <div key={item.item_id} className="border-b border-zinc-800 pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-sm text-zinc-400">
                          ${item.spent?.toLocaleString() || 0} / ${item.allocated.toLocaleString()}
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${Math.min((item.spent || 0) / item.allocated * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Expense Button */}
              {postGrant.budget_items?.length > 0 && (
                <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full mt-4 border-zinc-700" data-testid="add-expense-btn">
                      <Receipt className="w-4 h-4 mr-2" />
                      Add Expense
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-950 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle>Record Expense</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Budget Category *</Label>
                        <Select value={newExpense.budget_item_id} onValueChange={(v) => setNewExpense(prev => ({ ...prev, budget_item_id: v }))}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {postGrant.budget_items?.map((item) => (
                              <SelectItem key={item.item_id} value={item.item_id}>{item.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Description *</Label>
                        <Input 
                          value={newExpense.description}
                          onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="e.g., Office supplies"
                          className="bg-zinc-900 border-zinc-800"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount ($) *</Label>
                          <Input 
                            type="number"
                            value={newExpense.amount}
                            onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                            placeholder="100"
                            className="bg-zinc-900 border-zinc-800"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date *</Label>
                          <Input 
                            type="date"
                            value={newExpense.date}
                            onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Receipt/Notes</Label>
                        <Input 
                          value={newExpense.receipt_note}
                          onChange={(e) => setNewExpense(prev => ({ ...prev, receipt_note: e.target.value }))}
                          placeholder="Receipt #123 or notes"
                          className="bg-zinc-900 border-zinc-800"
                        />
                      </div>
                      <Button 
                        onClick={addExpense}
                        className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      >
                        Record Expense
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Recent Expenses */}
              {postGrant.expenses?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Recent Expenses</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {postGrant.expenses?.slice(-5).reverse().map((exp) => (
                      <div key={exp.expense_id} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-300">{exp.description}</span>
                        <span className="text-emerald-400 font-mono">${exp.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Milestones */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target weight="duotone" className="w-5 h-5 text-emerald-400" />
                  Milestones
                </CardTitle>
                <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="add-milestone-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-950 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle>Add Milestone</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input 
                          value={newMilestone.title}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Phase 1 Complete"
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="input-milestone-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea 
                          value={newMilestone.description}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe the milestone..."
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="input-milestone-desc"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Due Date *</Label>
                        <Input 
                          type="date"
                          value={newMilestone.due_date}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, due_date: e.target.value }))}
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="input-milestone-date"
                        />
                      </div>
                      <Button 
                        onClick={addMilestone}
                        className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                        data-testid="submit-milestone-btn"
                      >
                        Add Milestone
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {postGrant.milestones?.length === 0 ? (
                <p className="text-zinc-500 text-center py-4">No milestones yet</p>
              ) : (
                <div className="space-y-3">
                  {postGrant.milestones?.map((ms) => (
                    <div 
                      key={ms.milestone_id} 
                      className={`flex items-start gap-3 p-3 rounded-sm border ${ms.completed ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-zinc-800'}`}
                    >
                      <Checkbox 
                        checked={ms.completed}
                        onCheckedChange={() => toggleMilestone(ms.milestone_id, ms.completed)}
                        className="mt-1"
                        data-testid={`milestone-check-${ms.milestone_id}`}
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${ms.completed ? 'line-through text-zinc-500' : ''}`}>{ms.title}</p>
                        {ms.description && <p className="text-sm text-zinc-400 mt-1">{ms.description}</p>}
                        <div className="flex items-center gap-1 text-xs text-zinc-500 mt-2">
                          <Calendar className="w-3 h-3" />
                          Due: {new Date(ms.due_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Smart Budget Template (when generated) */}
        {smartBudget && (
          <Card className="bg-zinc-950 border-emerald-500/30" data-testid="smart-budget-template">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Sparkle weight="duotone" className="w-5 h-5 text-emerald-400" />
                  Smart Budget Template
                  <span className="text-xs text-zinc-500 font-normal ml-2">
                    {smartBudget.grant_category} &middot; ${smartBudget.awarded_amount?.toLocaleString()}
                  </span>
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSmartBudget(null)}
                    className="border-zinc-700 rounded-sm text-xs"
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => applyBudgetTemplate(smartBudget.budget_template)}
                    className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm text-xs"
                    data-testid="apply-budget-template-btn"
                  >
                    Apply Template
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {smartBudget.budget_template?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between border-b border-zinc-800 pb-3 last:border-0">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{item.name}</span>
                        <span className="text-emerald-400 font-mono text-sm">${item.allocated?.toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-zinc-500">{item.description}</p>
                    </div>
                    <span className="text-xs text-zinc-500 ml-4 font-mono w-12 text-right">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Spending Guide */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BookOpen weight="duotone" className="w-5 h-5 text-emerald-400" />
                Spending Guide
              </CardTitle>
              <div className="flex items-center gap-2">
                {spendingGuide && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate("/vendors")}
                    className="text-emerald-400 hover:text-emerald-300 text-xs"
                    data-testid="view-vendors-btn"
                  >
                    <Storefront className="w-4 h-4 mr-1" />
                    View SC Vendors
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={generateSpendingGuide}
                  disabled={generatingGuide}
                  className="bg-black border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm"
                  data-testid="spending-guide-btn"
                >
                  {generatingGuide ? (
                    <><div className="loading-spinner mr-2" style={{width:14,height:14}} />Generating...</>
                  ) : (
                    <><Sparkle className="w-4 h-4 mr-1" />{spendingGuide ? "Refresh" : "Generate Guide"}</>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {spendingGuide ? (
              <div className="prose prose-invert prose-sm max-w-none">
                <div className="text-xs text-zinc-500 mb-3 font-mono uppercase tracking-wider">
                  {spendingGuide.grant_category} &middot; {spendingGuide.funder_name}
                </div>
                <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed spending-guide-content">
                  {spendingGuide.spending_guide?.split('\n').map((line, i) => {
                    if (line.startsWith('## ')) {
                      return <h3 key={i} className="text-base font-bold text-emerald-400 mt-5 mb-2">{line.replace('## ', '')}</h3>;
                    }
                    if (line.startsWith('- ') || line.startsWith('* ')) {
                      return <p key={i} className="text-sm text-zinc-300 pl-4 mb-1">&bull; {line.replace(/^[-*]\s/, '')}</p>;
                    }
                    if (line.trim() === '') return null;
                    return <p key={i} className="text-sm text-zinc-300 mb-1">{line}</p>;
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
                <p className="text-zinc-500 text-sm">Generate a spending guide for compliance tips, best practices, and documentation requirements for this grant.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Flash Reports */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText weight="duotone" className="w-5 h-5 text-emerald-400" />
                Flash Reports
              </CardTitle>
              <div className="flex gap-2">
                {/* Generate Report */}
                <Dialog open={flashReportDialogOpen} onOpenChange={setFlashReportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-black border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm" data-testid="ai-report-btn">
                      <Sparkle className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Lightning className="w-5 h-5 text-emerald-400" />
                        Generate Flash Report
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-sm text-zinc-400">
                        Your budget, expenses, and milestones will be analyzed to generate a professional compliance report for your funder.
                      </p>
                      <div className="space-y-2">
                        <Label>Report Type</Label>
                        <Select value={flashReportType} onValueChange={setFlashReportType}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="progress">Progress Report</SelectItem>
                            <SelectItem value="quarterly">Quarterly Report</SelectItem>
                            <SelectItem value="final">Final Report</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Additional Notes (optional)</Label>
                        <Textarea 
                          value={flashReportNotes}
                          onChange={(e) => setFlashReportNotes(e.target.value)}
                          placeholder="Any specific accomplishments, challenges, or context to include..."
                          className="bg-zinc-900 border-zinc-800 min-h-20"
                        />
                      </div>
                      <Button 
                        onClick={generateFlashReport}
                        disabled={generatingReport}
                        className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      >
                        {generatingReport ? (
                          <>
                            <div className="loading-spinner mr-2" />
                            Generating Report...
                          </>
                        ) : (
                          <>
                            <Sparkle className="w-4 h-4 mr-2" />
                            Generate Report
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Manual Report */}
                <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700" data-testid="add-report-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Manual
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create Manual Report</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Report Title *</Label>
                        <Input 
                          value={newReport.title}
                          onChange={(e) => setNewReport(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Q1 Progress Report"
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="input-report-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Report Type</Label>
                        <Select value={newReport.report_type} onValueChange={(v) => setNewReport(prev => ({ ...prev, report_type: v }))}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-report-type">
                            <SelectValue />
                          </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="progress">Progress Report</SelectItem>
                          <SelectItem value="final">Final Report</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Content *</Label>
                      <Textarea 
                        value={newReport.content}
                        onChange={(e) => setNewReport(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Describe progress, accomplishments, challenges..."
                        className="bg-zinc-900 border-zinc-800 min-h-32"
                        data-testid="input-report-content"
                      />
                    </div>
                    <Button 
                      onClick={addReport}
                      className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      data-testid="submit-report-btn"
                    >
                      Create Report
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {postGrant.reports?.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No reports yet. Create your first Flash Report to document progress.</p>
            ) : (
              <div className="space-y-4">
                {postGrant.reports?.map((report) => (
                  <div key={report.report_id} className="border border-zinc-800 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{report.title}</h4>
                      <span className="badge-emerald text-xs px-2 py-0.5 rounded-sm uppercase">
                        {report.report_type}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 whitespace-pre-wrap">{report.content}</p>
                    <p className="text-xs text-zinc-500 mt-3">
                      Created: {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PostGrantDetail;
